import { useCapabilityRegistryContext } from '@mobile-mapping/providers';
import {
  MODEL_TRANSPARENCY_MIN_PERCENT,
  ViewsCapabilityProvider,
} from '@trimble/web3d-providers';
import { SelectionActionsCapability } from '@trimble/web3d-capabilities';
import { createMemo, JSX } from 'solid-js';
import { t } from '@trimble/i18n';
import {
  VisibilityControl,
  type SliderRowConfig,
  type VisibilityRowConfig,
} from './VisibilityControl';
import { MainToolbarButton } from '../main-toolbar/MainToolbarButton';
import {
  hasLoadedStations,
  resolveStationOpacityDisable,
} from '../helpers/pane-view-mode-helpers';

const VISIBILITY_TOOLTIP_KEY = 'mobileMapping.globalToolbar.visibility';

const POINT_CLOUD_MIN_OPACITY_PERCENT = 0;
const STATION_MIN_OPACITY_PERCENT = 0;
const MAX_OPACITY_PERCENT = 100;

const percentFromLevel = (level: number | null | undefined): number =>
  level ?? MAX_OPACITY_PERCENT;

const levelFromPercent = (
  percent: number,
  max = MAX_OPACITY_PERCENT
): number | null => (percent >= max ? null : percent);

/**
 * Main-toolbar entry point for global visibility. Bridges
 * {@link ViewsCapabilityProvider} (which owns cross-pane transparency
 * state) into the presentational {@link VisibilityControl}. Global
 * rows are never themselves "linkable" — they are the upstream that
 * per-pane rows link to.
 */
export const MainToolbarVisibilityControl = (): JSX.Element => {
  const { capabilityRegistry } = useCapabilityRegistryContext();

  const viewsProvider = (): ViewsCapabilityProvider | undefined =>
    capabilityRegistry.get(ViewsCapabilityProvider)();

  const rows = createMemo((): VisibilityRowConfig[] => {
    const models: SliderRowConfig = {
      kind: 'slider',
      id: 'models',
      labelKey: 'mobileMapping.transparency.models',
      sliderTestId: 'transparency-slider-models',
      min: MODEL_TRANSPARENCY_MIN_PERCENT,
      percent: () =>
        percentFromLevel(viewsProvider()?.getGlobalModelTransparencyLevel()),
      onChange: (percent) =>
        viewsProvider()?.updateGlobalModelTransparencyLevel(
          levelFromPercent(percent)
        ),
    };
    const pointClouds: SliderRowConfig = {
      kind: 'slider',
      id: 'point-clouds',
      labelKey: 'mobileMapping.transparency.pointClouds',
      sliderTestId: 'transparency-slider-point-clouds',
      min: POINT_CLOUD_MIN_OPACITY_PERCENT,
      percent: () =>
        percentFromLevel(
          viewsProvider()?.getGlobalPointCloudTransparencyLevel()
        ),
      onChange: (percent) =>
        viewsProvider()?.updateGlobalPointCloudTransparencyLevel(
          levelFromPercent(percent)
        ),
    };
    const stations: SliderRowConfig = {
      kind: 'slider',
      id: 'stations',
      labelKey: 'mobileMapping.transparency.stations',
      sliderTestId: 'transparency-slider-stations',
      min: STATION_MIN_OPACITY_PERCENT,
      percent: () =>
        percentFromLevel(viewsProvider()?.getGlobalStationTransparencyLevel()),
      isDisabled: () =>
        resolveStationOpacityDisable(hasLoadedStations(capabilityRegistry))
          .isDisabled,
      disabledTooltipKey: 'mobileMapping.transparency.stationsNotLoaded',
      onChange: (percent) =>
        viewsProvider()?.updateGlobalStationTransparencyLevel(
          levelFromPercent(percent)
        ),
    };
    return [models, pointClouds, stations];
  });

  /**
   * "Show all objects" un-hides any model entities the user had hidden
   * via per-pane selection actions ("Hide selection" / "Show only
   * selected"). Transparency sliders are left untouched on purpose:
   * partially-transparent objects are still considered "shown" — the
   * user only loses them by hiding them entirely. Fans the action out
   * across every pane's {@link SelectionActionsCapability}.
   */
  const resetAll = (): void => {
    for (const pane of capabilityRegistry.paneContainers()) {
      const selectionActions = pane.get(SelectionActionsCapability)();
      void selectionActions?.showAll();
    }
  };

  return (
    <VisibilityControl
      rows={rows}
      resetAll={resetAll}
      buttonSize="medium"
      controlTestId="global-visibility-control"
      renderTrigger={({ isOpen, toggle }) => (
        <MainToolbarButton
          iconName="tc-icon-eye-visibility-toggle"
          ariaLabel={t(VISIBILITY_TOOLTIP_KEY)}
          tooltip={t(VISIBILITY_TOOLTIP_KEY)}
          size="medium"
          pressed={isOpen()}
          action={toggle}
        />
      )}
    />
  );
};
